import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const PLATFORMS = [
  { key: 'eleven', label: '11번가' },
  { key: 'esm', label: 'ESM(지마켓/옥션)' },
  { key: 'sellerhub', label: '셀러허브' },
  { key: 'coupang', label: '쿠팡' },
  { key: 'toss', label: '토스쇼핑' },
];

export default function OpenmarketMatching() {
  const [platform, setPlatform] = useState('eleven');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expanded, setExpanded] = useState({}); // key: "대분류" or "대분류>중분류"
  // [기능 추가 - 2026-09-27] 11번가는 elevenst_categories.json 파일에 없는 대분류(반려동물
  // 등)가 실전에서 종종 발견됐다. 검색이 안 되면, 11번가 전체 카테고리 캐시(12,314개)에서
  // 이름으로 검색해서 즉시 추가할 수 있게 한다.
  const [fullSearchKeyword, setFullSearchKeyword] = useState('');
  const [fullSearchResults, setFullSearchResults] = useState([]);
  const [fullSearching, setFullSearching] = useState(false);
  const [addingFromFull, setAddingFromFull] = useState(null);

  const doFullSearch = async (keyword) => {
    setFullSearchKeyword(keyword);
    if (!keyword.trim()) { setFullSearchResults([]); return; }
    setFullSearching(true);
    try {
      const r = await api.get('/categories/openmarket-mappings/eleven-full-search', { params: { keyword: keyword.trim() } });
      setFullSearchResults(r.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFullSearching(false);
    }
  };

  const addFromFullSearch = async (item) => {
    setAddingFromFull(item.dispNo);
    try {
      const r = await api.post('/categories/openmarket-mappings/eleven-add-from-full', item);
      alert(r.data.message);
      doSearch(searchKeyword);
    } catch (err) {
      alert('추가 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setAddingFromFull(null);
    }
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/categories/openmarket-mappings', { params: { platform } });
      setRows(r.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [platform]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filteredRows = useMemo(() => rows.filter(r => {
    const ourPath = [r.large_category, r.middle_category, r.small_category, r.detail_category].filter(Boolean).join(' > ');
    if (search && !ourPath.includes(search)) return false;
    if (filter === 'matched' && !r.platform_category_no) return false;
    if (filter === 'unmatched' && r.platform_category_no) return false;
    return true;
  }), [rows, search, filter]);

  // 대분류 > 중분류로 그룹핑
  const tree = useMemo(() => {
    const t = {};
    filteredRows.forEach(r => {
      const large = r.large_category || '(미분류)';
      const middle = r.middle_category || '(미분류)';
      if (!t[large]) t[large] = {};
      if (!t[large][middle]) t[large][middle] = [];
      t[large][middle].push(r);
    });
    return t;
  }, [filteredRows]);

  // 검색어가 있으면 해당 그룹 자동 펼침
  useEffect(() => {
    if (!search) return;
    const next = {};
    Object.entries(tree).forEach(([large, middles]) => {
      next[large] = true;
      Object.keys(middles).forEach(middle => { next[`${large}>${middle}`] = true; });
    });
    setExpanded(prev => ({ ...prev, ...next }));
  }, [search, tree]);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const matchedCount = rows.filter(r => r.platform_category_no).length;
  const unmatchedCount = rows.length - matchedCount;

  const openEdit = (row) => { setEditingId(row.id); setSearchKeyword(''); setSearchResults([]); };

  const doSearch = async (keyword) => {
    setSearchKeyword(keyword);
    if (!keyword) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get('/categories/openmarket-mappings/search', { params: { platform, keyword } });
      setSearchResults(r.data || []);
    } catch (err) { console.error(err); } finally { setSearching(false); }
  };

  const selectMatch = async (row, candidate) => {
    try {
      await api.post('/categories/openmarket-mappings', {
        category_id: row.id, platform,
        platform_category_no: candidate.dispNo, platform_category_name: candidate.dispNm,
      });
      setEditingId(null);
      fetchRows();
    } catch (err) { console.error(err); alert('저장 실패'); }
  };

  const matchTypeBadge = (matchType) => {
    if (!matchType) return null;
    if (matchType === 'auto_exact') return { text: '자동 매칭', bg: '#EAF3DE', color: '#173404' };
    if (matchType === 'manual_confirmed') return { text: '확정됨', bg: '#E6F1FB', color: '#042C53' };
    return { text: '확인 필요', bg: '#FAEEDA', color: '#412402' };
  };

  const groupProgress = (items) => {
    const matched = items.filter(i => i.platform_category_no).length;
    return `${matched}/${items.length}`;
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '4px' }}>오픈마켓 매칭</h2>
      <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '16px' }}>
        스마트스토어 카테고리는 이 페이지가 아니라 카테고리 관리에서 매칭합니다.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        {PLATFORMS.map(p => (
          <button key={p.key} onClick={() => setPlatform(p.key)}
            style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              border: platform === p.key ? '1px solid #378ADD' : '1px solid #ddd',
              color: platform === p.key ? '#185FA5' : '#888', background: '#fff', fontWeight: platform === p.key ? '500' : '400' }}
          >{p.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '13px', color: '#888' }}>매칭완료 {matchedCount} · 미매칭 {unmatchedCount}</span>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input type="text" placeholder="카테고리명으로 검색" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: '140px', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}>
          <option value="all">전체</option>
          <option value="unmatched">미매칭만</option>
          <option value="matched">매칭완료만</option>
        </select>
      </div>

      {/* [기능 추가 - 2026-09-27] 11번가는 elevenst_categories.json에 없는 대분류(반려동물 등)가
          실전에서 종종 발견됐다. 위 검색으로 못 찾을 경우를 위해, 화면 상단에 항상 보이는
          "11번가 전체 카테고리 검색+추가" 섹션(전체 15,295개 캐시에서 검색). */}
      {platform === 'eleven' && (
        <div style={{ padding: '12px 14px', border: '1px dashed #ddd', borderRadius: '8px', marginBottom: '16px', background: '#fafafa' }}>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
            위 검색으로 못 찾을 경우, 11번가 전체 카테고리(약 12,000개)에서 검색해 바로 추가할 수 있습니다.
          </div>
          <input type="text" placeholder="11번가 전체 카테고리에서 검색 (예: 건식사료)" value={fullSearchKeyword}
            onChange={e => doFullSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }} />
          {fullSearching && <div style={{ fontSize: '12px', color: '#aaa' }}>검색 중...</div>}
          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {fullSearchResults.map(item => (
              <div key={item.dispNo} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', fontSize: '12px', borderRadius: '6px', background: '#fff', border: '1px solid #eee', marginBottom: '4px' }}>
                <span>{item.fullPath}</span>
                <button disabled={addingFromFull === item.dispNo} onClick={() => addFromFullSearch(item)}
                  style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '1px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '8px' }}>
                  {addingFromFull === item.dispNo ? '추가 중...' : '추가'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>불러오는 중...</div>
      ) : (
        <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
          {Object.entries(tree).map(([large, middles]) => {
            const largeItems = Object.values(middles).flat();
            const largeOpen = !!expanded[large];
            return (
              <div key={large}>
                <div onClick={() => toggle(large)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#fafafa', cursor: 'pointer', borderTop: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}>
                    <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: largeOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                    {large}
                  </div>
                  <span style={{ fontSize: '12px', color: '#888' }}>{groupProgress(largeItems)} 매칭</span>
                </div>
                {largeOpen && Object.entries(middles).map(([middle, items]) => {
                  const middleKey = `${large}>${middle}`;
                  const middleOpen = !!expanded[middleKey];
                  return (
                    <div key={middle} style={{ paddingLeft: '20px' }}>
                      <div onClick={() => toggle(middleKey)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px 10px 0', cursor: 'pointer', borderTop: '1px solid #f2f2f2' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555' }}>
                          <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: middleOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                          {middle}
                        </div>
                        <span style={{ fontSize: '11px', color: '#aaa' }}>{groupProgress(items)}</span>
                      </div>
                      {middleOpen && (
                        <div style={{ paddingLeft: '20px' }}>
                          {items.map(row => {
                            const ourName = row.detail_category || row.small_category;
                            const badge = matchTypeBadge(row.match_type);
                            return (
                              <div key={row.id}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 80px', alignItems: 'center', padding: '8px 14px 8px 0', borderTop: '1px solid #f7f7f7', fontSize: '13px' }}>
                                  <div>{ourName}</div>
                                  <div>
                                    {row.platform_category_no ? (
                                      <>
                                        {row.platform_category_name}
                                        {badge && <span style={{ fontSize: '10px', background: badge.bg, color: badge.color, padding: '1px 6px', borderRadius: '6px', marginLeft: '6px' }}>{badge.text}</span>}
                                      </>
                                    ) : (
                                      <span style={{ color: '#aaa' }}>미매칭</span>
                                    )}
                                  </div>
                                  <button onClick={() => openEdit(row)} style={{ fontSize: '12px', padding: '4px 10px', justifySelf: 'end', borderRadius: '6px', border: row.platform_category_no ? '1px solid #ddd' : '1px solid #378ADD', color: row.platform_category_no ? '#555' : '#185FA5', background: '#fff', cursor: 'pointer' }}>
                                    {row.platform_category_no ? '수정' : '매칭'}
                                  </button>
                                </div>
                                {editingId === row.id && (
                                  <div style={{ padding: '10px 14px', background: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
                                    <input type="text" autoFocus placeholder="카테고리명 검색" value={searchKeyword} onChange={e => doSearch(e.target.value)}
                                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }} />
                                    {searching && <div style={{ fontSize: '12px', color: '#aaa' }}>검색 중...</div>}
                                    <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                      {searchResults.map(c => (
                                        <div key={c.dispNo} onClick={() => selectMatch(row, c)}
                                          style={{ padding: '8px 10px', fontSize: '13px', cursor: 'pointer', borderRadius: '6px', background: '#fff', border: '1px solid #eee', marginBottom: '4px' }}
                                        >{c.dispNm} <span style={{ color: '#aaa', fontSize: '11px' }}>({c.group})</span></div>
                                      ))}
                                    </div>

                                    <button onClick={() => setEditingId(null)} style={{ marginTop: '6px', fontSize: '12px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>닫기</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
